import { useMeeting, useParticipant, usePubSub } from "@videosdk.live/react-sdk";
import { useEffect, useRef } from "react";
import { useAppContext } from "../AppContextDef";
import usePubsubFunction from "../utils/usePubsubFunction";

// Recorder-side network watchdog: if the customer's INCOMING video bitrate stays
// below this for LOW_BITRATE_STREAK consecutive 1s samples, we treat the network
// as unstable. Lives here (always mounted on the recorder) so the verdict
// survives page transitions instead of remounting per page section.
const LOW_BITRATE_KBPS = 30;
const LOW_BITRATE_STREAK = 5;

const PubSubHandler = () => {
  const {
    setAllPubsubData,
    setCurrentPageData,
    setFaceVerified,
    setAgreedData,
    setFaceDetectError,
    setMediaPermissionError,
    setIsAgreeButtonClick,
    setIsDisagreeButtonClick,
    setIsTimerRunningData,
    setJourneyConfigData,
    setJourneyPubsubData,
    setTimerData,
    setRunningTimeData,
    setSelectedLang,
    setIsVideoConsentStart,
    setOpenVideoErr,
    setOpenVideoFailErr,
    setIsAbortSignal,
    setIsJourneyEnded,
    setOpenDeviceChange,
    setOpenErrorBox,
    setOpenTextMatchErr,
    setAccuracy,
    setOpenJourneyLimitBox,
    setQnaPubSubData,
    setTerminationFlag,
    setDelegatedApiSuccess,
    isMeetingEnded,
    currentPageData,
    isTimerRunningData,
    setIsRecorderNetworkUnstable,
  } = useAppContext();

  // ---- recorder network watchdog -----------------------------------------
  // Watch the customer's INCOMING video bitrate continuously. If it stays at ~0
  // for LOW_BITRATE_STREAK consecutive seconds, set our own context flag (drives
  // the recorder dialog) and publish NETWORK_UNSTABLE so the client mirrors it
  // (dialog + consent stop/disable). On recovery we clear and publish false.
  // We also watch the inbound freezeCount: a NEW freeze while on the VideoConsent
  // screen publishes VIDEO_FREEZE so the client can restart an in-progress
  // consent recording (frames arriving but stalling — a bitrate check can miss it).
  const pubsubPublishMethod = usePubsubFunction();
  const { publish: networkUnstablePublish } = usePubSub(`NETWORK_UNSTABLE`);
  const { publish: videoFreezePublish } = usePubSub(`VIDEO_FREEZE`);
  const { participants } = useMeeting({});
  const nonLocalParticipantId = Array.from(participants.values()).find(
    (p) => !p.local
  )?.id;
  const { getVideoStats, webcamOn } = useParticipant(nonLocalParticipantId);

  const recorderLowBitrateCountRef = useRef(0);
  const recorderUnstableShownRef = useRef(false);
  // freezeCount from getVideoStats is CUMULATIVE for the whole session, so it
  // can't be compared to a fixed value. We snapshot it when a consent recording
  // starts (the baseline) and treat any later increase as a freeze that happened
  // *during* this recording. Reset to null between recordings so each attempt
  // re-baselines; a drop below the baseline (track renegotiated → counter reset)
  // re-baselines too, so we never miss post-reset freezes or fire false ones.
  const freezeBaselineRef = useRef(null);
  // Fresh values for the interval closure (which is created once on mount and
  // never restarts on page/recording changes).
  const currentPageDataRef = useRef(currentPageData);
  const isRecordingRef = useRef(!!isTimerRunningData?.isRunning);
  useEffect(() => {
    currentPageDataRef.current = currentPageData;
  }, [currentPageData]);
  useEffect(() => {
    isRecordingRef.current = !!isTimerRunningData?.isRunning;
  }, [isTimerRunningData]);

  useEffect(() => {
    const setUnstable = (isUnstable) => {
      setIsRecorderNetworkUnstable(isUnstable);
      pubsubPublishMethod(
        networkUnstablePublish,
        "NetworkUnstable",
        { persist: true },
        { isUnstable }
      );
    };

    if (!webcamOn || !getVideoStats || !nonLocalParticipantId || isMeetingEnded) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const stats = await getVideoStats();
        const s = stats && stats.length ? stats[0] : undefined;
        const bitrate = s?.bitrate ?? 0;
        const freezeCount = s?.freezeCount ?? 0;
        console.log(
          `[Recorder] customer incoming video bitrate: ${bitrate} (low if < ${LOW_BITRATE_KBPS}), freezeCount: ${freezeCount}`
        );

        // Freeze detection — only while a consent recording is actually in
        // progress. freezeCount is cumulative, so we baseline it on the first
        // in-recording tick and treat a later increase as a freeze DURING this
        // recording, asking the client to restart consent.
        if (
          currentPageDataRef.current === "VideoConsent" &&
          isRecordingRef.current
        ) {
          if (
            freezeBaselineRef.current === null ||
            freezeCount < freezeBaselineRef.current
          ) {
            // first tick of this recording, or the counter reset → (re)baseline
            freezeBaselineRef.current = freezeCount;
          } else if (freezeCount > freezeBaselineRef.current) {
            console.warn(
              "[Recorder] customer video freeze during consent recording — requesting restart",
              { freezeCount, baseline: freezeBaselineRef.current }
            );
            pubsubPublishMethod(
              videoFreezePublish,
              "VideoFreeze",
              { persist: false },
              { frozen: true, freezeCount, timestamp: Date.now() }
            );
            // advance the baseline so we don't re-fire every tick for the same
            // accumulated count (the client restarts on the first event anyway)
            freezeBaselineRef.current = freezeCount;
          }
        } else {
          // not in an active consent recording — drop any stale baseline
          freezeBaselineRef.current = null;
        }

        if (bitrate < LOW_BITRATE_KBPS) {
          if (recorderLowBitrateCountRef.current >= LOW_BITRATE_STREAK) {
            recorderLowBitrateCountRef.current = 0;
          }
          recorderLowBitrateCountRef.current += 1;
          if (
            recorderLowBitrateCountRef.current >= LOW_BITRATE_STREAK &&
            !recorderUnstableShownRef.current
          ) {
            recorderUnstableShownRef.current = true;
            console.warn(
              "[Recorder] customer video bitrate low for 5s — flagging network unstable"
            );
            setUnstable(true);
          }
        } else if (
          recorderLowBitrateCountRef.current > 0 ||
          recorderUnstableShownRef.current
        ) {
          recorderLowBitrateCountRef.current = 0;
          if (recorderUnstableShownRef.current) {
            recorderUnstableShownRef.current = false;
            console.log("[Recorder] customer video bitrate recovered");
            setUnstable(false);
          }
        }
      } catch (err) {
        console.error("[Recorder] failed to read customer video stats", err);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      recorderLowBitrateCountRef.current = 0;
      freezeBaselineRef.current = null;
      if (recorderUnstableShownRef.current) {
        recorderUnstableShownRef.current = false;
        setUnstable(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamOn, getVideoStats, nonLocalParticipantId, isMeetingEnded]);

  usePubSub("CURRENT_PAGE", {
    onMessageReceived: ({ payload }) => {
      setCurrentPageData(payload?.message);
      // if (handlePageDataFromChild) {
      //   handlePageDataFromChild(message?.message);
      // }
      setFaceVerified(false);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setCurrentPageData(latestMessage.payload?.message);
      }
    },
  });

  usePubSub("SELECT_LANG", {
    onMessageReceived: ({ message }) => {
      setSelectedLang({ lang: message, timestamp: Date.now() });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setSelectedLang({ lang: latestMessage.message, timestamp: Date.now() });
      }
    },
  });

  usePubSub("ALL_DETAILS", {
    onMessageReceived: ({ payload }) => {
      setAllPubsubData((prev) => {
        return {
          ...(prev || {}),
          ...payload.message,
          qna: payload?.qna,
        };
      });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setAllPubsubData({ ...latestMessage.payload?.message, qna: latestMessage.payload?.qna });
      }
    },
  });

  usePubSub("JOURNEY_CONFIG_DATA", {
    onMessageReceived: ({ payload }) => {
      setJourneyConfigData(payload);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setJourneyConfigData(latestMessage.payload);
      }
    },
  });

  usePubSub("JOURNEY_DATA", {
    onMessageReceived: ({ payload }) => {
      setJourneyPubsubData(payload);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setJourneyPubsubData(latestMessage.payload);
      }
    },
  });

  usePubSub("MEDIA_ERROR", {
    onMessageReceived: ({ payload }) => {
      setMediaPermissionError(payload.err);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setMediaPermissionError(latestMessage.payload.err);
      }
    },
  });

  usePubSub("FACE_DETECT_ERROR", {
    onMessageReceived: ({ payload }) => {
      setFaceDetectError({ err: payload.err, timestamp: Date.now() });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setFaceDetectError({
          err: latestMessage.payload.err,
          timestamp: Date.now(),
        });
      }
    },
  });

  usePubSub("FACE_VERIFIED", {
    onMessageReceived: ({ payload }) => {
      setFaceVerified({
        isVerified: payload.isVerified,
        timestamp: Date.now(),
      });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setFaceVerified({
          isVerified: latestMessage.payload.isVerified,
          timestamp: Date.now(),
        });
      }
    },
  });

  usePubSub("AGREED", {
    onMessageReceived: ({ payload }) => {
      setAgreedData(payload.isAgreed);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setAgreedData(latestMessage.payload.isAgreed);
      }
    },
  });

  usePubSub("START_VIDEO", {
    onMessageReceived: ({ payload }) => {
      setIsVideoConsentStart(payload.isStart);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsVideoConsentStart(latestMessage.payload.isStart);
      }
    },
  });

  usePubSub("AGREE", {
    onMessageReceived: ({ payload }) => {
      setIsAgreeButtonClick({
        isAgreeClick: payload.isAgreeClick,
        currentPage: payload.currentPage,
      });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsAgreeButtonClick({
          isAgreeClick: latestMessage.payload.isAgreeClick,
          currentPage: latestMessage.payload.currentPage,
        });
      }
    },
  });

  usePubSub("DISAGREE", {
    onMessageReceived: ({ payload }) => {
      setIsDisagreeButtonClick({
        isDisagreeClick: payload.isDisagreeClick,
        currentPage: payload.currentPage,
      });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsDisagreeButtonClick({
          isDisagreeClick: latestMessage.payload.isDisagreeClick,
          currentPage: latestMessage.payload.currentPage,
        });
      }
    },
  });

  usePubSub("TIMER_RUNNING", {
    onMessageReceived: ({ payload }) => {
      setIsTimerRunningData({
        isRunning: payload.isRunning,
        timestamp: new Date(),
      });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsTimerRunningData({
          isRunning: latestMessage.payload.isRunning,
          timestamp: new Date(),
        });
      }
    },
  });


  usePubSub("RUNNING_TIME_DATA", {
    onMessageReceived: ({ message }) => {
      setRunningTimeData(message);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setRunningTimeData(latestMessage.message);
      }
    },
  });

  usePubSub("TIMER_DATA", {
    onMessageReceived: ({ payload }) => {
      setTimerData(payload.time);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setTimerData(latestMessage.payload.time);
      }
    },
  });


  usePubSub("VIDEO_RECORDING_ATTEMPT_ERR", {
    onMessageReceived: ({ payload }) => {
      setOpenVideoErr(payload.open);
      setAccuracy(payload.accuracy)
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenVideoErr(latestMessage.payload.open);
      }
    },
  });

  usePubSub("TEXT_MATCHING_FAILED", {
    onMessageReceived: ({ payload }) => {
      setOpenTextMatchErr(payload.open);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenTextMatchErr(latestMessage.payload.open);
      }
    },
  })

  usePubSub("VIDEO_RECORDING_ATTEMPT_FAIL_ERR", {
    onMessageReceived: ({ payload }) => {
      setOpenVideoFailErr(payload.open);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenVideoFailErr(latestMessage.payload.open);
      }
    },
  });

  usePubSub("DEVICE_CHANGED", {
    onMessageReceived: ({ payload }) => {
      setOpenDeviceChange(payload.open);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenDeviceChange(latestMessage.payload.open);
      }
    },
  });

  usePubSub("ERROR_MEETING_LEFT", {
    onMessageReceived: ({ payload }) => {
      setOpenErrorBox(payload.open);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenErrorBox(latestMessage.payload.open);
      }
    },
  });

  usePubSub("JOURNEY_LIMIT", {
    onMessageReceived: ({ payload }) => {
      setOpenJourneyLimitBox(payload.showModal);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setOpenJourneyLimitBox(latestMessage.payload.showModal);
      }
    },
  });



  usePubSub("ABORT_SIGNAL", {
    onMessageReceived: ({ payload }) => {
      setIsAbortSignal(payload.isAbort);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsAbortSignal(latestMessage.payload.isAbort);
      }
    },
  });

  usePubSub("AUDIO_PRELOADED", {
    onMessageReceived: ({ message }) => {
    },
    onOldMessagesReceived: (messages) => {
    },
  });

  usePubSub("PRELOADED_AUDIO_SPOKEN", {
    onMessageReceived: ({ message }) => {
    },
    onOldMessagesReceived: (messages) => {
    },
  });

  usePubSub("END_JOURNEY_API_WAITING", {
    onMessageReceived: ({ payload }) => {
      setIsJourneyEnded(payload.isJourneyEnd);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setIsJourneyEnded(latestMessage.payload.isJourneyEnd);
      }
    },
  });

  usePubSub("TERMINATION_STATUS", {
    onMessageReceived: ({ payload }) => {
      setTerminationFlag(payload.showTerminationMessage === true);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setTerminationFlag(latestMessage.payload.showTerminationMessage === true);
      }
    },
  });

  usePubSub("DELEGATED_API_SUCCESS", {
    onMessageReceived: ({ payload }) => {
      setDelegatedApiSuccess({ ...payload, timestamp: Date.now() });
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setDelegatedApiSuccess({
          ...latestMessage.payload,
          timestamp: Date.now(),
        });
      }
    },
  });

  usePubSub("QNA_DATA", {
    onMessageReceived: ({ payload }) => {
      setQnaPubSubData(payload);
    },
    onOldMessagesReceived: (messages) => {
      const latestMessage = messages.sort((a, b) => {
        if (a.timestamp > b.timestamp) {
          return -1;
        }
        if (a.timestamp < b.timestamp) {
          return 1;
        }
        return 0;
      })[0];

      if (latestMessage) {
        setQnaPubSubData(latestMessage.payload);
      }
    },
  });

  return <></>;
};

export default PubSubHandler;
